import { UserPath } from '../types/UserPath';
import { getChallengeOption } from '../data/challengeOptions';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY_PREFIX = 'lyra-user-path';
const getStorageKey = (userId?: string) => `${STORAGE_KEY_PREFIX}-${userId || 'anon'}`;
const isValidUuid = (value?: string) =>
  typeof value === 'string' &&
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value);
const DEFAULT_FOCUS = 'Communication/Fundraising';
const DEFAULT_CHALLENGE = 'A1';
const DEFAULT_STYLE = 'quick_win';

type SupabasePathFetch =
  | { status: 'ok'; path: UserPath | null }
  | { status: 'error'; path: null; error: string };

/**
 * Helper to convert challenge option learning path to lesson ID array.
 */
const getLessonIdsFromChallenge = (challengeId: string): string[] => {
  const challengeOption = getChallengeOption(challengeId);
  if (!challengeOption) {
    console.warn(`No challenge option found for ${challengeId}, using fallback path`);
    return ['daily-pulse']; // fallback to available lesson
  }

  return challengeOption.learningPath.map(step => step.id);
};

/**
 * Get the quick win lesson ID for a challenge.
 */
const getQuickWinId = (challengeId: string): string | undefined => {
  const challengeOption = getChallengeOption(challengeId);
  return challengeOption?.quickWinId;
};

const persistLocally = (path: UserPath) => {
  if (typeof window === 'undefined') return;
  try {
    const key = getStorageKey(path.user_id);
    localStorage.setItem(key, JSON.stringify(path));
  } catch (error) {
    console.warn('[userPathService] Failed to persist path locally', error);
  }
};

const loadLocalPath = (userId?: string): UserPath | null => {
  if (typeof window === 'undefined') return null;
  try {
    const key = getStorageKey(userId);
    const legacyKey = STORAGE_KEY_PREFIX; // Legacy key without user scoping
    const raw = localStorage.getItem(key) || localStorage.getItem(legacyKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserPath;

    // Migrate legacy key to user-scoped key for authenticated users
    if (userId && !localStorage.getItem(key)) {
      localStorage.setItem(key, raw);
      localStorage.removeItem(legacyKey);
    }

    return parsed;
  } catch (error) {
    console.warn('[userPathService] Failed to load path from localStorage', error);
    return null;
  }
};

export const persistToSupabase = async (path: UserPath) => {
  if (!isValidUuid(path.user_id)) {
    console.warn('[userPathService] Skipping Supabase persist due to invalid user_id', path.user_id);
    return;
  }

  try {
    const payload = {
      user_id: path.user_id,
      focus: path.focus,
      challenge: path.challenge,
      path: path.path,
      quick_win: path.quickWin ?? null,
      style: path.style ?? null,
      current_index: path.current_index,
      phase: path.phase,
      path_version: path.path_version ?? 1,
      why_this_next: path.why_this_next ?? null,
      alt_paths: path.alt_paths ?? null,
      last_personalized_at: path.last_personalized_at ?? new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const result = await supabase
      .from('user_paths' as any)
      .upsert(payload, { onConflict: 'user_id' });

    if (result.error) {
      // If the table does not exist or RLS blocks, log and continue with local persistence only
      console.warn('[userPathService] Supabase upsert failed, using local path only', result.error);
    }
  } catch (error) {
    console.warn('[userPathService] Failed to persist path to Supabase, using local only', error);
  }
};

const fetchFromSupabase = async (userId: string): Promise<SupabasePathFetch> => {
  if (!isValidUuid(userId)) {
    console.warn('[userPathService] Skipping Supabase fetch due to invalid user_id', userId);
    return { status: 'error', path: null, error: 'invalid-user-id' };
  }

  try {
    const { data, error } = await supabase
      .from('user_paths' as any)
      .select('*')
      .eq('user_id', userId)
      .limit(1);

    if (error) {
      console.warn('[userPathService] Supabase fetch failed, falling back to local', error);
      return { status: 'error', path: null, error: error.message };
    }

    if (!data || data.length === 0) return { status: 'ok', path: null };
    const record = data[0] as Record<string, any>;
    const challengeOption = getChallengeOption(record.challenge);
    const defaultWhy = challengeOption
      ? challengeOption.learningPath.reduce((acc, step) => {
        acc[step.id] = step.why;
        return acc;
      }, {} as Record<string, string>)
      : {};

    return {
      status: 'ok',
      path: {
        user_id: record.user_id,
        focus: record.focus,
        challenge: record.challenge,
        path: record.path || [],
        quickWin: record.quick_win || record.quickWin,
        style: record.style,
        phase: record.phase || 'structured',
        current_index: record.current_index ?? 0,
        path_version: record.path_version,
        why_this_next: record.why_this_next || record.whyThisNext || defaultWhy,
        alt_paths: record.alt_paths || record.altPaths || [],
        last_personalized_at: record.last_personalized_at,
        created_at: record.created_at,
        updated_at: record.updated_at
      }
    };
  } catch (error) {
    console.warn('[userPathService] Unexpected Supabase error, using local path', error);
    return { status: 'error', path: null, error: (error as Error).message };
  }
};

const createFallbackPath = async (userId: string, persistRemotely: boolean): Promise<UserPath> => {
  const fallback = await createPath(DEFAULT_FOCUS, DEFAULT_CHALLENGE, {
    userId,
    style: DEFAULT_STYLE,
    persist: false
  });

  if (persistRemotely) {
    void persistToSupabase(fallback);
  }

  return fallback;
};

export const getCachedPathForUser = (userId?: string): UserPath | null => {
  const local = loadLocalPath(userId);

  if (local && userId && local.user_id !== userId && isValidUuid(userId)) {
    const reboundPath: UserPath = { ...local, user_id: userId };
    persistLocally(reboundPath);
    return reboundPath;
  }

  return local;
};

export const refreshPathForUser = async (userId?: string): Promise<UserPath | null> => {
  if (!userId) {
    return loadLocalPath(userId);
  }

  const remote = await fetchFromSupabase(userId);
  if (remote.status === 'ok' && remote.path) {
    const migrated = await migratePathIfNeeded(remote.path);
    persistLocally(migrated);
    return migrated;
  }

  const local = loadLocalPath(userId);
  if (local) {
    const reboundPath =
      local.user_id !== userId && isValidUuid(userId)
        ? { ...local, user_id: userId }
        : local;
    const migrated = await migratePathIfNeeded(reboundPath, {
      persistRemote: remote.status === 'ok'
    });
    persistLocally(migrated);
    if (remote.status === 'ok' && !remote.path) {
      void persistToSupabase(migrated);
    }
    return migrated;
  }

  if (remote.status === 'ok' && !remote.path && isValidUuid(userId)) {
    return await createFallbackPath(userId, true);
  }

  if (remote.status === 'error' && isValidUuid(userId)) {
    return await createFallbackPath(userId, false);
  }

  return null;
};

export interface CreatePathOptions {
  userId?: string;
  style?: string;
  persist?: boolean;
}

export const createPath = async (
  focus: string,
  challenge: string,
  options: CreatePathOptions = {}
): Promise<UserPath> => {
  const lessonIds = getLessonIdsFromChallenge(challenge);
  const quickWinId = getQuickWinId(challenge);
  const challengeOption = getChallengeOption(challenge);

  const whyMap = challengeOption
    ? challengeOption.learningPath.reduce((acc, step) => {
      acc[step.id] = step.why;
      return acc;
    }, {} as Record<string, string>)
    : {};

  // If the user asked for a quick win, surface it first.
  const orderedPath =
    options.style === 'quick_win' && quickWinId
      ? [quickWinId, ...lessonIds.filter(id => id !== quickWinId)]
      : lessonIds;

  const userPath: UserPath = {
    user_id: options.userId || 'anon',
    focus,
    challenge,
    path: orderedPath,
    quickWin: quickWinId,
    style: options.style,
    phase: options.style === 'exploration' ? 'daily' : 'structured',
    current_index: 0,
    path_version: 2, // Version 2: Integrated new pedagogical archetypes
    why_this_next: whyMap,
    alt_paths: [],
    last_personalized_at: new Date().toISOString()
  };

  persistLocally(userPath);
  if (options.persist !== false) {
    await persistToSupabase(userPath);
  }

  return userPath;
};

// Check for path updates and migrate if necessary
const migratePathIfNeeded = async (
  path: UserPath,
  options: { persistRemote?: boolean } = {}
): Promise<UserPath> => {
  const CURRENT_PATH_VERSION = 3;
  const shouldPersistRemote = options.persistRemote !== false;

  // If path is old, regenerate the lesson list from the current challenge definition
  if ((path.path_version || 1) < CURRENT_PATH_VERSION) {
    console.log(`[userPathService] Migrating path from v${path.path_version || 1} to v${CURRENT_PATH_VERSION}`);

    // Get the fresh lesson list from the source of truth
    const newLessonIds = getLessonIdsFromChallenge(path.challenge);
    const quickWinId = getQuickWinId(path.challenge);

    // Re-apply quick win logic if it was present
    const orderedPath =
      path.style === 'quick_win' && quickWinId
        ? [quickWinId, ...newLessonIds.filter(id => id !== quickWinId)]
        : newLessonIds;

    const updatedPath: UserPath = {
      ...path,
      path: orderedPath,
      path_version: CURRENT_PATH_VERSION,
      updated_at: new Date().toISOString()
    };

    // Persist the update
    persistLocally(updatedPath);
    if (shouldPersistRemote) {
      await persistToSupabase(updatedPath);
    }

    return updatedPath;
  }

  return path;
};

export const loadPathForUser = async (userId?: string): Promise<UserPath | null> => {
  let remoteStatus: SupabasePathFetch | null = null;
  // Prefer Supabase for authenticated users; fallback to local storage.
  if (userId) {
    remoteStatus = await fetchFromSupabase(userId);
    if (remoteStatus.status === 'ok' && remoteStatus.path) {
      const migrated = await migratePathIfNeeded(remoteStatus.path);
      persistLocally(migrated);
      return migrated;
    }
  }

  const local = loadLocalPath(userId);

  // If we loaded a legacy/mock/anon path and now have a real user, rebind it.
  if (local && userId && local.user_id !== userId && isValidUuid(userId)) {
    const reboundPath: UserPath = { ...local, user_id: userId };
    const migrated = await migratePathIfNeeded(reboundPath, {
      persistRemote: remoteStatus?.status === 'ok'
    });
    persistLocally(migrated);
    if (remoteStatus?.status === 'ok') {
      await persistToSupabase(migrated);
    }
    return migrated;
  }

  if (local) {
    return await migratePathIfNeeded(local, {
      persistRemote: remoteStatus?.status === 'ok'
    });
  }

  return local;
};

export const clearLocalPath = (userId?: string) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch (error) {
    console.warn('[userPathService] Failed to clear local path', error);
  }
};

export const advancePathIndex = async (sessionId: string, userId?: string): Promise<UserPath | null> => {
  const currentPath = await loadPathForUser(userId);
  if (!currentPath) return null;

  const currentIndex = currentPath.current_index ?? 0;
  const completedLessonIndex = currentPath.path.indexOf(sessionId);

  // If lesson not in path, no advancement needed
  if (completedLessonIndex === -1) return currentPath;

  // Allow skip-ahead: advance to the lesson after the completed one if it's ahead of current
  const nextIndex = Math.max(
    currentIndex,
    Math.min(completedLessonIndex + 1, currentPath.path.length - 1)
  );

  // No change needed
  if (nextIndex === currentIndex) return currentPath;

  const updated: UserPath = {
    ...currentPath,
    current_index: nextIndex,
    last_personalized_at: new Date().toISOString()
  };

  persistLocally(updated);
  await persistToSupabase(updated);
  return updated;
};
